import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaSave,
  FaTimes,
  FaTrophy,
  FaGripVertical,
  FaGlobe,
  FaUpload,
  FaImage
} from 'react-icons/fa';
import './AdminAchievements.css';

const AdminAchievements = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [achievementsList, setAchievementsList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    number: '',
    label_en: '',
    label_fr: '',
    iconUrl: '',
    order: 0
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchAchievements();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchAchievements = async () => {
    try {
      const q = query(collection(db, 'achievements'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAchievementsList(items);
    } catch (err) {
      console.error('Error fetching achievements:', err);
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        setError('Firestore permissions not configured. Please update Firestore rules to include "achievements" collection.');
      } else {
        setError('Failed to load achievements');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      number: '',
      label_en: '',
      label_fr: '',
      iconUrl: '',
      order: achievementsList.length
    });
    setIconFile(null);
    setIconPreview('');
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEdit = (item) => {
    setFormData({
      number: item.number || '',
      label_en: item.label_en || item.label || '',
      label_fr: item.label_fr || '',
      iconUrl: item.iconUrl || '',
      order: item.order || 0
    });
    setIconPreview(item.iconUrl || '');
    setIconFile(null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('svg') && !file.name.endsWith('.svg')) {
      setError('Please upload an SVG file.');
      return;
    }

    if (file.size > 500 * 1024) {
      setError('File size must be under 500KB.');
      return;
    }

    setIconFile(file);
    setError('');

    const reader = new FileReader();
    reader.onload = (ev) => {
      setIconPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadIcon = async (file) => {
    const fileName = `achievements/${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, fileName);
    await uploadBytes(fileRef, file);
    const downloadURL = await getDownloadURL(fileRef);
    return downloadURL;
  };

  const deleteOldIcon = async (iconUrl) => {
    if (!iconUrl || !iconUrl.includes('firebase')) return;
    try {
      const fileRef = storageRef(storage, iconUrl);
      await deleteObject(fileRef);
    } catch (err) {
      console.log('Could not delete old icon:', err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.number.trim() || !formData.label_en.trim()) {
      setError('Number and English label are required.');
      setActiveTab('en');
      return;
    }

    if (!iconFile && !formData.iconUrl) {
      setError('Please upload an icon.');
      return;
    }

    setSaving(true);

    try {
      let iconUrl = formData.iconUrl;

      if (iconFile) {
        setUploading(true);
        if (editingId && formData.iconUrl) {
          await deleteOldIcon(formData.iconUrl);
        }
        iconUrl = await uploadIcon(iconFile);
        setUploading(false);
      }

      const achievementData = {
        number: formData.number.trim(),
        label_en: formData.label_en.trim(),
        label_fr: formData.label_fr.trim(),
        label: formData.label_en.trim(),
        iconUrl: iconUrl,
        order: formData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'achievements', editingId), achievementData);
        setSuccess('Achievement updated successfully!');
      } else {
        achievementData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'achievements'), achievementData);
        setSuccess('Achievement added successfully!');
      }

      await fetchAchievements();
      resetForm();
    } catch (err) {
      console.error('Error saving achievement:', err);
      setError(err.message || 'Failed to save achievement. Please try again.');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.number} - ${item.label_en || item.label}"?`)) {
      return;
    }

    try {
      if (item.iconUrl) {
        await deleteOldIcon(item.iconUrl);
      }
      await deleteDoc(doc(db, 'achievements', item.id));
      setSuccess('Achievement deleted successfully!');
      await fetchAchievements();
    } catch (err) {
      console.error('Error deleting achievement:', err);
      setError('Failed to delete achievement');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-achievements">
      <header className="admin-achievements-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaTrophy /> Nos Réalisations (Achievements)</h1>
      </header>

      <main className="admin-achievements-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!showForm ? (
          <>
            <div className="achievements-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New Achievement
              </button>
            </div>

            <div className="achievements-list">
              {achievementsList.length === 0 ? (
                <div className="empty-state">
                  <FaTrophy />
                  <h3>No achievements yet</h3>
                  <p>Click "Add New Achievement" to create your first item.</p>
                </div>
              ) : (
                achievementsList.map((item, index) => (
                  <div key={item.id} className="admin-achievement-item">
                    <div className="admin-achievement-item-order">
                      <FaGripVertical />
                      <span>{index + 1}</span>
                    </div>
                    <div className="admin-achievement-item-icon">
                      {item.iconUrl ? (
                        <img src={item.iconUrl} alt="" className="admin-achievement-icon-img" />
                      ) : (
                        <FaImage />
                      )}
                    </div>
                    <div className="admin-achievement-item-number">
                      {item.number}
                    </div>
                    <div className="admin-achievement-item-content">
                      <div className="admin-achievement-item-langs">
                        <span className="lang-badge en">EN: {item.label_en || item.label || 'Not set'}</span>
                        <span className="lang-badge fr">FR: {item.label_fr || 'Not set'}</span>
                      </div>
                    </div>
                    <div className="admin-achievement-item-actions">
                      <button onClick={() => handleEdit(item)} className="edit-btn">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => handleDelete(item)} className="delete-btn">
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="achievements-form-container">
            <h2>{editingId ? 'Edit Achievement' : 'Add New Achievement'}</h2>

            {/* Language Tabs */}
            <div className="language-tabs">
              <button
                type="button"
                className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
                onClick={() => setActiveTab('en')}
              >
                <FaGlobe /> English
              </button>
              <button
                type="button"
                className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
                onClick={() => setActiveTab('fr')}
              >
                <FaGlobe /> French
              </button>
            </div>

            <form onSubmit={handleSubmit} className="achievements-form">
              {/* English Content */}
              <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Label (English) *</label>
                  <input
                    type="text"
                    value={formData.label_en}
                    onChange={(e) => setFormData({ ...formData, label_en: e.target.value })}
                    placeholder="e.g., Projects Completed"
                  />
                </div>
              </div>

              {/* French Content */}
              <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
                <div className="form-group">
                  <label>Label (French)</label>
                  <input
                    type="text"
                    value={formData.label_fr}
                    onChange={(e) => setFormData({ ...formData, label_fr: e.target.value })}
                    placeholder="e.g., Projets réalisés"
                  />
                </div>
              </div>

              {/* Common Fields (always visible) */}
              <div className="common-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label>Number/Value *</label>
                    <input
                      type="text"
                      value={formData.number}
                      onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                      placeholder="e.g., 50+, 25M€, 10,000+"
                    />
                    <small className="form-hint">Can include symbols like +, €, M, etc.</small>
                  </div>

                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Icon (SVG) *</label>
                  <div className="icon-upload-area">
                    {iconPreview ? (
                      <div className="icon-preview-container">
                        <img src={iconPreview} alt="Icon preview" className="icon-preview-img" />
                        <button
                          type="button"
                          className="icon-remove-btn"
                          onClick={() => {
                            setIconFile(null);
                            setIconPreview('');
                            setFormData({ ...formData, iconUrl: '' });
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="icon-upload-placeholder"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FaUpload />
                        <span>Click to upload SVG icon</span>
                        <small>Max 500KB, SVG format only</small>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".svg,image/svg+xml"
                      onChange={handleIconFileChange}
                      className="icon-file-input"
                    />
                    {iconPreview && (
                      <button
                        type="button"
                        className="icon-change-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FaUpload /> Change Icon
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <FaTimes /> Cancel
                </button>
                <button type="submit" disabled={saving} className="save-btn">
                  {saving ? (
                    <><FaSpinner className="spinner" /> {uploading ? 'Uploading...' : 'Saving...'}</>
                  ) : (
                    <><FaSave /> {editingId ? 'Update' : 'Save'} Achievement</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAchievements;
